import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { login, prepareLoginForm } from '../services/api/AuthService';
import { getCurrentUser } from '../services/api/UserService';
import '../styles/Login.css';
import ircLogo from '../assets/img/as-finder-logo.svg'; // IRCのロゴ画像
import mailAddressIcon from '../assets/img/mail-address.svg';
import passwordIcon from '../assets/img/pass-word.svg';

const LoginPage = ({ userStr, userType }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [credentials, setCredentials] = useState({
    accountname: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // CSRF対策関連の状態
  const [csrfReady, setCsrfReady] = useState(false);
  const [csrfError, setCsrfError] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  // コンポーネントマウント時にCSRFトークンを準備
  useEffect(() => {
    const prepareCsrfToken = async () => {
      try {
        setIsInitializing(true);
        setCsrfError(false);
        
        await prepareLoginForm();
        setCsrfReady(true);
      } catch (error) {
        console.error('CSRFトークンの準備に失敗しました:', error);
        setCsrfError(true);
        setCsrfReady(false);
        
        // CSRF準備失敗の場合の補助メッセージ
        setError('セキュリティ設定の準備に失敗しました。ページを再読み込みしてください。');
      } finally {
        setIsInitializing(false);
      }
    };

    prepareCsrfToken();
  }, []);

  // CSRF準備失敗時の再試行機能
  const retryCSRFPreparation = async () => {
    setError('');
    setCsrfError(false);
    setIsInitializing(true);
    
    try {
      await prepareLoginForm();
      setCsrfReady(true);
      console.log('CSRFトークンの再準備が成功しました');
    } catch (error) {
      setCsrfError(true);
      setError('セキュリティ設定の準備に失敗しました。しばらく時間を置いて再度お試しください。');
    } finally {
      setIsInitializing(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCredentials({
      ...credentials,
      [name]: value
    });
    if (error) {
      setError('');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    // CSRF準備チェック
    if (!csrfReady) {
      setError('セキュリティ設定が完了していません。しばらくお待ちください。');
      return;
    }
    
    setError('');
    setIsLoading(true);

    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('Login attempt:', { accountname: credentials.accountname });
      }
      const response = await login(credentials.accountname, credentials.password, userStr);

      if (response?.access_token) {
        localStorage.setItem('token', response.access_token);
        const userResponse = await getCurrentUser();
        localStorage.setItem('userType', userType);
        localStorage.setItem('userInfo', JSON.stringify(userResponse));

        // 仮パスワード状態の場合の処理
        if (response.reset_token != null) {
          const resetToken = response.reset_token; // 仮パスワード状態のトークンを取得
          localStorage.setItem('password_change_required', 'true');
          
          // 直接パスワード設定画面に遷移
          navigate(`/password-reset?token=${resetToken}`);
          
          return;
        }

        // パスワード変更が不要な場合は通常の遷移
        localStorage.removeItem('password_change_required');
        if (userType === 'admin') {
          navigate('/admin/agency/search');
        } else if (userType === 'agency') {
          const agencyId = JSON.parse(localStorage.getItem('userInfo')).agency_id;
          if (agencyId !== null) {
            navigate(`/agency/detail/${agencyId}`);
          } else {
            navigate('/agency/search');
          }
        } else if (userType === 'solicitor') {
          navigate('/solicitor/insurance/system/search');
        }
      }
    } catch (err) {
      // エラーダイアログ表示はBaseApiServiceで行う
      // エラーコンソール出力は各ApiServiceで行う
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // ログインボタンの無効化判定
  const isLoginDisabled = isLoading || !csrfReady || isInitializing || csrfError;

  return (
    <div className="login-container">
      <div className="login-background" />
      <div className="login-card">
        <div className="logo-header">
          <img src={ircLogo} alt="IRC 株式会社アイリックコーポレーション" className="main-logo" />
          <div className="c-label c-bg-pale-blue">{userStr}</div>
        </div>
        
        {/* CSRF初期化中の表示 */}
        {isInitializing && (
          <div className="csrf-loading-message">
            <div className="loading-spinner"></div>
            <span>セキュリティ設定を準備中...</span>
          </div>
        )}
        
        {/* CSRFエラー時の表示 */}
        {csrfError && !isInitializing && (
          <div className="csrf-error-message">
            <div className="error-content">
              <span>セキュリティ設定でエラーが発生しました</span>
              <button 
                type="button" 
                className="retry-button"
                onClick={retryCSRFPreparation}
                disabled={isInitializing}
              >
                再試行
              </button>
            </div>
          </div>
        )}
        
        {/* 通常のエラーメッセージ */}
        {error && <div className="error-message">{error}</div>}
        
        <form onSubmit={handleLogin} className="login-form">
          <div className="input-group">
            <div className="icon-container">
              <img src={mailAddressIcon} alt="メールアドレス" className="icon user-icon" />
            </div>
            <input
              type="email"
              name="accountname"
              className="login-box"
              value={credentials.accountname}
              onChange={handleInputChange}
              placeholder="アカウント名（メールアドレス）"
              disabled={!csrfReady || isInitializing}
              required
            />
          </div>
          <div className="input-group">
            <div className="icon-container">
              <img src={passwordIcon} alt="パスワード" className="icon lock-icon" />
            </div>
            <input
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={credentials.password}
              onChange={handleInputChange}
              placeholder="パスワード"
              disabled={!csrfReady || isInitializing}
              required
            />
            <div 
              className="password-toggle" 
              onClick={togglePasswordVisibility}
              style={{ 
                opacity: (!csrfReady || isInitializing) ? 0.5 : 1,
                pointerEvents: (!csrfReady || isInitializing) ? 'none' : 'auto'
              }}
            >
              <svg className="eye-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                <path d={showPassword 
                  ? "M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3-1.34 3-3 3z" 
                  : "M12 6c3.79 0 7.17 2.13 8.82 5.5-.59 1.22-1.42 2.27-2.41 3.12l1.41 1.41c1.39-1.23 2.49-2.77 3.18-4.53-1.73-4.39-6-7.5-11-7.5-1.27 0-2.49.2-3.64.57l1.65 1.65C10.66 6.09 11.32 6 12 6zm-1.07 1.14L13 9.21c.57.25 1.03.71 1.28 1.28l2.07 2.07c.08-.34.14-.7.14-1.07C16.5 9.01 14.48 7 12 7c-.37 0-.72.05-1.07.14zM2.01 3.87l2.68 2.68C3.06 7.83 1.77 9.53 1 11.5 2.73 15.89 7 19 12 19c1.52 0 2.98-.29 4.32-.82l3.42 3.42 1.41-1.41L3.42 2.45 2.01 3.87zm7.5 7.5l2.61 2.61c-.04.01-.08.02-.12.02-1.38 0-2.5-1.12-2.5-2.5 0-.05.01-.08.01-.13zm-3.4-3.4l1.75 1.75c-.23.55-.36 1.15-.36 1.78 0 2.48 2.02 4.5 4.5 4.5.63 0 1.23-.13 1.77-.36l.98.98c-.88.24-1.8.38-2.75.38-3.79 0-7.17-2.13-8.82-5.5.7-1.43 1.72-2.61 2.93-3.53z"}
                  fill="#999"/>
              </svg>
            </div>
          </div>
          <div className="password-help">
            <Link to="/password-reset-request" className="help-link">パスワードを忘れた方</Link>
          </div>
          <button 
            type="submit" 
            className="login-button" 
            disabled={isLoginDisabled}
          >
            {isLoading ? 'ログイン中...' 
             : isInitializing ? 'セキュリティ設定準備中...'
             : csrfError ? 'セキュリティ設定エラー'
             : 'ログイン'}
          </button>
        </form>

        {/* SSO Login Section - 募集人のみ表示 */}
        {userType === 'solicitor' && (
          <div className="sso-section">
            <div className="sso-divider">
              <span>または</span>
            </div>
            <div className="sso-message">
              AS platform ユーザーはこちら
            </div>
            <button 
              type="button"
              className="sso-button"
              onClick={() => {
                const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:7000';
                window.location.href = `${apiUrl}/sso/entry`;
              }}
            >
              ログイン
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginPage;